import React, { useState } from 'react';
import {
    View, Text, StyleSheet, TextInput, FlatList,
    TouchableOpacity, ActivityIndicator, Alert, Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { searchUsers } from '../../../api/social';
import { postData } from '../../../services/api';
import UserAvatar from '../../../components/social/shared/UserAvatar';

const CreateGroupScreen = ({ navigation }) => {
    const insets = useSafeAreaInsets();
    const [groupName, setGroupName] = useState('');
    const [query, setQuery] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searching, setSearching] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSearch = async (text) => {
        setQuery(text);
        if (text.length < 2) {
            setUsers([]);
            return;
        }

        setSearching(true);
        try {
            const data = await searchUsers(text);
            const filteredData = data.filter(u => !selectedUsers.find(s => s.id === u.id));
            setUsers(filteredData);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setSearching(false);
        }
    };

    const toggleUser = (user) => {
        if (selectedUsers.find(u => u.id === user.id)) {
            setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
        } else {
            setSelectedUsers([...selectedUsers, user]);
            setUsers(users.filter(u => u.id !== user.id));
        }
    };

    const handleCreateGroup = async () => {
        if (!groupName.trim()) {
            Alert.alert('Group Name', 'Please enter a name for the group.');
            return;
        }
        if (selectedUsers.length === 0) {
            Alert.alert('Participants', 'Please select at least one other participant.');
            return;
        }

        setLoading(true);
        try {
            const participantIds = selectedUsers.map(u => u.id);
            const response = await postData('/api/chat/rooms/group', {
                name: groupName,
                participantIds
            });

            navigation.replace('ChatRoom', {
                roomId: response.id,
                room: response
            });
        } catch (error) {
            console.error('Create group error:', error);
            Alert.alert('Error', 'Failed to create group.');
        } finally {
            setLoading(false);
        }
    };

    const renderSelectedUser = ({ item }) => (
        <View style={styles.selectedUser}>
            <UserAvatar source={item.profileImageUrl} name={item.name} size={42} />
            <TouchableOpacity style={styles.removeUser} onPress={() => toggleUser(item)}>
                <Ionicons name="close-circle" size={18} color="#EF4444" />
            </TouchableOpacity>
        </View>
    );

    return (
        <View style={styles.container}>
            {/* Social-themed header */}
            <View style={[styles.headerWrapper, { paddingTop: insets.top }]}>
                <LinearGradient
                    colors={['#0c4a6e', '#0369a1', '#0891b2']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={StyleSheet.absoluteFill}
                />
                <View style={styles.orbLarge} />
                <View style={styles.orbSmall} />

                <View style={styles.headerContent}>
                    <TouchableOpacity
                        style={styles.backBtn}
                        onPress={() => navigation.goBack()}
                    >
                        <Ionicons name="arrow-back" size={22} color="rgba(255,255,255,0.9)" />
                    </TouchableOpacity>

                    <View style={styles.headerCenter}>
                        <Text style={styles.headerTitle}>New Group</Text>
                        <Text style={styles.headerSubtitle}>Create a community chat</Text>
                    </View>

                    <TouchableOpacity
                        style={[styles.createHeaderBtn, (loading || selectedUsers.length === 0 || !groupName.trim()) && styles.createHeaderBtnDisabled]}
                        onPress={handleCreateGroup}
                        disabled={loading || selectedUsers.length === 0 || !groupName.trim()}
                    >
                        {loading ? <ActivityIndicator size="small" color="#FFFFFF" /> : <Text style={styles.createBtnText}>Create</Text>}
                    </TouchableOpacity>
                </View>
            </View>

            <View style={styles.formContainer}>
                <View style={styles.inputCard}>
                    <Ionicons name="people-outline" size={20} color="#0891b2" style={styles.inputIcon} />
                    <TextInput
                        style={styles.groupNameInput}
                        placeholder="Group Name"
                        placeholderTextColor="#9CA3AF"
                        value={groupName}
                        onChangeText={setGroupName}
                    />
                </View>
            </View>

            {selectedUsers.length > 0 && (
                <View style={styles.selectedListContainer}>
                    <Text style={styles.sectionLabel}>Participants ({selectedUsers.length})</Text>
                    <FlatList
                        horizontal
                        data={selectedUsers}
                        renderItem={renderSelectedUser}
                        keyExtractor={item => item.id.toString()}
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={styles.selectedList}
                    />
                </View>
            )}

            <View style={styles.searchContainer}>
                <Ionicons name="search" size={18} color="#0891b2" />
                <TextInput
                    style={styles.searchInput}
                    placeholder="Add participants..."
                    placeholderTextColor="#9CA3AF"
                    value={query}
                    onChangeText={handleSearch}
                />
            </View>

            {searching ? (
                <View style={styles.loaderContainer}>
                    <ActivityIndicator color="#06b6d4" size="large" />
                </View>
            ) : (
                <FlatList
                    data={users}
                    keyExtractor={item => item.id.toString()}
                    contentContainerStyle={styles.resultsList}
                    renderItem={({ item }) => (
                        <TouchableOpacity style={styles.userItem} onPress={() => toggleUser(item)}>
                            <UserAvatar source={item.profileImageUrl} name={item.name} size={46} />
                            <View style={styles.userInfo}>
                                <Text style={styles.userName}>{item.name}</Text>
                                <Text style={styles.userStatus}>{item.isOnline ? '🟢 Online' : 'Offline'}</Text>
                            </View>
                            <Ionicons name="add-circle" size={28} color="#0891b2" />
                        </TouchableOpacity>
                    )}
                    ListEmptyComponent={
                        query.length >= 2 ? (
                            <View style={styles.emptyContainer}>
                                <Text style={styles.emptyText}>No users found for "{query}"</Text>
                            </View>
                        ) : null
                    }
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F0FDFF',
    },
    headerWrapper: {
        overflow: 'hidden',
        paddingBottom: 12,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingTop: Platform.OS === 'ios' ? 12 : 18,
        paddingBottom: 12,
        gap: 12,
    },
    backBtn: {
        width: 38,
        height: 38,
        borderRadius: 11,
        backgroundColor: 'rgba(255,255,255,0.12)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerCenter: {
        flex: 1,
    },
    headerTitle: {
        fontSize: 20,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    headerSubtitle: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.6)',
        fontWeight: '500',
        textTransform: 'uppercase',
    },
    createHeaderBtn: {
        backgroundColor: 'rgba(255,255,255,0.2)',
        paddingHorizontal: 18,
        paddingVertical: 8,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
    },
    createHeaderBtnDisabled: {
        opacity: 0.5,
    },
    createBtnText: {
        color: '#FFFFFF',
        fontWeight: 'bold',
    },
    orbLarge: {
        position: 'absolute',
        width: 120,
        height: 120,
        borderRadius: 60,
        backgroundColor: 'rgba(34,211,238,0.1)',
        right: -30,
        top: -40,
    },
    orbSmall: {
        position: 'absolute',
        width: 60,
        height: 60,
        borderRadius: 30,
        backgroundColor: 'rgba(8,145,178,0.18)',
        left: '40%',
        bottom: -10,
    },
    formContainer: {
        padding: 16,
    },
    inputCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        paddingHorizontal: 16,
        height: 56,
        shadowColor: '#0891b2',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
    },
    inputIcon: {
        marginRight: 12,
    },
    groupNameInput: {
        flex: 1,
        fontSize: 16,
        fontWeight: '600',
        color: '#111827',
    },
    sectionLabel: {
        fontSize: 12,
        fontWeight: '700',
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 10,
        paddingHorizontal: 16,
    },
    selectedListContainer: {
        paddingVertical: 10,
        backgroundColor: '#FFFFFF',
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(6,182,212,0.08)',
    },
    selectedList: {
        paddingHorizontal: 16,
        gap: 12,
    },
    selectedUser: {
        position: 'relative',
    },
    removeUser: {
        position: 'absolute',
        top: -2,
        right: -2,
        backgroundColor: '#FFFFFF',
        borderRadius: 10,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        margin: 16,
        borderRadius: 12,
        paddingHorizontal: 16,
        height: 48,
        borderWidth: 1,
        borderColor: 'rgba(6,182,212,0.12)',
    },
    searchInput: {
        flex: 1,
        marginLeft: 10,
        fontSize: 15,
        color: '#111827',
    },
    resultsList: {
        paddingHorizontal: 16,
        paddingBottom: 20,
    },
    userItem: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        padding: 12,
        marginBottom: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 2,
        elevation: 1,
        gap: 12,
    },
    userInfo: {
        flex: 1,
    },
    userName: {
        fontSize: 15,
        fontWeight: '700',
        color: '#111827',
    },
    userStatus: {
        fontSize: 12,
        color: '#6B7280',
        marginTop: 2,
    },
    loaderContainer: {
        marginTop: 40,
        alignItems: 'center',
    },
    emptyContainer: {
        marginTop: 40,
        alignItems: 'center',
    },
    emptyText: {
        color: '#9CA3AF',
        fontSize: 14,
    },
});

export default CreateGroupScreen;
